import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WorkOrder } from '../../types';
import { assetLabel } from '../../utils/assetLabel';
import { workOrderApi } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import { Table, Button, message, Card, Tooltip, Tag, Input, Modal, Space } from 'antd';
import {
    ArrowLeftOutlined,
    RotateLeftOutlined,
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined
} from '@ant-design/icons';


const ArchivedWorkOrders: React.FC = () => {
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Ref to hold the timeout ID for debouncing
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchArchivedWorkOrders = async (search?: string) => {
        setLoading(true);
        try {
            // Shared axios client → refresh-token interceptor handles 401s.
            const response = await workOrderApi.getArchived(search);
            setWorkOrders(response.data);
        } catch (error) {
            console.error('Error fetching archived work orders:', error);
            message.error('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArchivedWorkOrders();
        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);

        // Debounce logic
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchArchivedWorkOrders(value);
        }, 500);
    };

    const handleUnarchive = async (workOrder: WorkOrder) => {
        try {
            // Shared axios client → refresh-token interceptor handles 401s.
            await workOrderApi.unarchive(workOrder.id!);
            message.success(`Work Order ${workOrder.wo_number} unarchived successfully`);
            fetchArchivedWorkOrders(searchText); // Refresh list with current search
        } catch (error) {
            console.error('Error unarchiving work order:', error);
            message.error('Error connecting to server');
        }
    };

    // Derive the role-based route prefix from the current path
    const getBasePath = () => {
        const currentPath = location.pathname;
        if (currentPath.startsWith('/owner')) {
            return '/owner';
        } else if (currentPath.startsWith('/advisor')) {
            return '/advisor';
        } else if (currentPath.startsWith('/technician')) {
            return '/technician';
        }
        return '/admin';
    };

    const handleViewDetails = (workOrder: WorkOrder) => {
        navigate(`${getBasePath()}/work-orders/${workOrder.id}`);
    };

    const columns = [
        {
            title: 'WO Number',
            dataIndex: 'wo_number',
            key: 'wo_number',
            render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
            sorter: (a: WorkOrder, b: WorkOrder) => a.wo_number.localeCompare(b.wo_number),
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_: any, record: WorkOrder) => {
                if (record.customer) {
                    return record.customer.company_name || `${record.customer.first_name} ${record.customer.last_name}`;
                }
                return 'Unknown Customer';
            },
        },
        {
            title: 'Vehicle',
            key: 'vehicle',
            render: (_: any, record: WorkOrder) => {
                if (record.vehicle) {
                    return assetLabel(record.vehicle);
                }
                return 'Unknown Vehicle';
            }
        },
        {
            title: 'Date',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (date: string) => new Date(date).toLocaleDateString(),
            sorter: (a: WorkOrder, b: WorkOrder) => new Date(a.updated_at || '').getTime() - new Date(b.updated_at || '').getTime(),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Total',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'completed' ? 'green' : 'default'}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: WorkOrder) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetails(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Unarchive (Restore to active list)">
                        <Button
                            icon={<RotateLeftOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchive(record);
                            }}
                            size="small"
                        >
                            Unarchive
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`${getBasePath()}/work-orders`)}
                    >
                        Back to Board
                    </Button>
                    <h1 style={{ margin: 0, fontSize: 24 }}>Archived Work Orders</h1>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        placeholder="Search archives..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={handleSearch}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchArchivedWorkOrders(searchText)}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={workOrders}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 800 }}
                    onRow={(record) => ({
                        onClick: () => handleViewDetails(record),
                        style: { cursor: 'pointer' }
                    })}
                />
            </Card>


        </div>
    );
};

export default ArchivedWorkOrders;
